import { Controller } from "@hotwired/stimulus";
import axios from "axios";

export default class extends Controller {
  static targets = [
    "input",
    "hiddenInput",
    "suggestions",
    "display",
    "textdisplay",
    "latitude",
    "longitude",
    "address",
  ];
  static values = { endpoint: String, local: Boolean, types: String };

  connect() {
    this.debounceTimer = null;
  }

  preventSubmit(e) {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  }

  onInput(e) {
    clearTimeout(this.debounceTimer);
    const query = e.target.value.trim();
    if (query.length < 3) {
      this.suggestionsTarget.classList.add("hidden");
      return;
    }
    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(query);
    }, 300);
  }

  async fetchSuggestions(query) {
    try {
      let url = `${this.endpointValue}?q=${encodeURIComponent(query)}`;
      if (this.hasTypesValue && this.typesValue) {
        url += `&types=${encodeURIComponent(this.typesValue)}`;
      }
      const { data } = await axios.get(url);
      this.displaySuggestions(data);
    } catch (error) {
      this.suggestionsTarget.classList.add("hidden");
    }
  }

  displaySuggestions(places) {
    this.suggestionsTarget.replaceChildren();

    if (!Array.isArray(places) || places.length === 0) {
      const empty = document.createElement("div");
      empty.className = "p-2 text-xs text-gray-500";
      empty.textContent = "Aucun résultat";
      this.suggestionsTarget.appendChild(empty);
      this.suggestionsTarget.classList.remove("hidden");
      return;
    }

    for (const place of places) {
      if (!place || !place.place_id) continue;

      const item = document.createElement("div");
      item.className = "p-2 hover:bg-gray-100 cursor-pointer text-xs";
      item.dataset.action = `click->${this.identifier}#selectPlace`;
      item.dataset.placeId = place.place_id;
      item.textContent =
        place.description ||
        place.structured_formatting?.main_text ||
        place.place_id;
      this.suggestionsTarget.appendChild(item);
    }

    this.suggestionsTarget.classList.remove("hidden");
  }

  async selectPlace(e) {
    const placeId = e.currentTarget.dataset.placeId;
    if (!placeId) {
      this.suggestionsTarget.classList.add("hidden");
      return;
    }
    const fallbackText = e.currentTarget.textContent.trim();
    try {
      const { data } = await axios.post(this.endpointValue, {
        place_id: placeId,
      });

      if (!data || !data.id) {
        console.error("Place details response missing id", data);
        this.suggestionsTarget.classList.add("hidden");
        return;
      }

      const labelField =
        this.element.dataset.labelFieldName || "formatted_address";
      const displayText =
        data[labelField] || data.formatted_address || fallbackText;

      // Set the hidden input value (this is what gets submitted)
      if (!this.localValue) {
        this.hiddenInputTarget.value = data.id;
      }

      // Update the visible text input with the display text
      this.inputTarget.value = displayText;

      // Update the overlay display
      this.textdisplayTarget.innerText = displayText;
      this.displayTarget.classList.remove("hidden");
      this.suggestionsTarget.classList.add("hidden");

      // If local mode, populate the additional hidden fields
      if (
        this.localValue &&
        this.hasLatitudeTarget &&
        this.hasLongitudeTarget &&
        this.hasAddressTarget
      ) {
        this.latitudeTarget.value = data.latitude || "";
        this.longitudeTarget.value = data.longitude || "";
        this.addressTarget.value = data.formatted_address || "";
      }

      this.dispatch("placeSelected", { detail: data });
    } catch (error) {
      console.error("Error fetching place details:", error);
    }

    this.suggestionsTarget.classList.add("hidden");
  }

  // Hide suggestions when clicking outside
  clickOutside(e) {
    if (!this.element.contains(e.target)) {
      this.suggestionsTarget.classList.add("hidden");
    }
  }

  clear(e) {
    e.preventDefault();
    this.displayTarget.classList.add("hidden");
    this.inputTarget.value = "";
    this.hiddenInputTarget.value = "";

    // Clear local fields if in local mode
    if (this.localValue) {
      if (this.hasLatitudeTarget) this.latitudeTarget.value = "";
      if (this.hasLongitudeTarget) this.longitudeTarget.value = "";
      if (this.hasAddressTarget) this.addressTarget.value = "";
    }

    this.inputTarget.focus();
  }
}
